import type { MetadataRegistryEntry } from '@compass/client/types/metadata_registry';
import { SlidersHorizontal } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { MetadataFilterControl } from './MetadataFilterControl';

interface MetadataAdvancedFiltersProps {
  /** Advanced-filter definitions, already allow-listed and ordered (see METADATA_FILTER_KEYS). */
  definitions: MetadataRegistryEntry[];
  /** Current selections keyed by metadata key -> selected values. */
  selectedValues: Record<string, string[]>;
  /** Emits the new selection for one key. */
  onChange: (key: string, values: string[]) => void;
  organizationId?: string | null;
  programIds?: string[];
  /** Call-start window as UTC ISO strings (already timezone-resolved). */
  callStartTimeAfter?: string;
  callStartTimeBefore?: string;
  /** Start expanded when there are active selections (controlled by the caller). */
  defaultExpanded?: boolean;
}

/** Number of keys with at least one active selection. */
function countActive(
  definitions: MetadataRegistryEntry[],
  selectedValues: Record<string, string[]>,
): number {
  return definitions.reduce((count, entry) => {
    const key = entry.key;
    return count + (key && (selectedValues[key]?.length ?? 0) > 0 ? 1 : 0);
  }, 0);
}

/**
 * Per-org metadata advanced filters (AQ-496), split into an inline toggle and a
 * below-the-row panel so the "Advanced filters" button can sit in the SAME row as
 * the base filters (Programs/Agents/date) while the controls expand underneath.
 *
 * Usage: render `toggle` inside the base filter row and `panel` as a sibling
 * below it. Both are driven by shared `expanded` state owned here.
 *
 * Returns `{ toggle, panel }` — or `{ toggle: null, panel: null }` when the org
 * has no searchable evaluation metadata keys.
 */
export function useMetadataAdvancedFilters({
  definitions,
  selectedValues,
  onChange,
  organizationId,
  programIds,
  callStartTimeAfter,
  callStartTimeBefore,
  defaultExpanded = false,
}: MetadataAdvancedFiltersProps): {
  toggle: React.ReactNode;
  panel: React.ReactNode;
} {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  const activeCount = React.useMemo(
    () => countActive(definitions, selectedValues),
    [definitions, selectedValues],
  );

  if (definitions.length === 0) {
    return { toggle: null, panel: null };
  }

  const toggle = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="gap-2 text-muted-foreground"
      onClick={() => setExpanded(prev => !prev)}
      aria-expanded={expanded}
    >
      <SlidersHorizontal className="h-4 w-4" />
      Advanced filters
      {activeCount > 0 ? (
        <Badge variant="secondary" className="ml-1">
          {activeCount}
        </Badge>
      ) : null}
    </Button>
  );

  const panel = expanded ? (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/40 p-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Advanced
      </span>
      {definitions.map(entry => {
        const key = entry.key;
        if (!key) return null;

        return (
          <MetadataFilterControl
            key={key}
            entry={entry}
            selected={selectedValues[key] ?? []}
            onChange={values => onChange(key, values)}
            organizationId={organizationId}
            programIds={programIds}
            callStartTimeAfter={callStartTimeAfter}
            callStartTimeBefore={callStartTimeBefore}
          />
        );
      })}
    </div>
  ) : null;

  return { toggle, panel };
}
